import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

interface ChatMessage {
  id: string
  role: 'student' | 'tutor'
  content: string
  createdAt: string
}

interface PersistedAppState {
  version: 1
  completedTaskIds: string[]
  messages: ChatMessage[]
}

interface AppStateValue {
  completedTaskIds: ReadonlySet<string>
  messages: readonly ChatMessage[]
  toggleTask: (taskId: string) => void
  addStudentMessage: (content: string) => void
  resetLocalState: () => void
}

const STORAGE_KEY = 'studna-dashboard:v1'

const INITIAL_MESSAGES: readonly ChatMessage[] = [
  {
    id: 'welcome',
    role: 'tutor',
    content: '先告诉我你的思路，我会逐步提示，而不是直接给答案。',
    createdAt: '2026-07-14T08:00:00.000Z',
  },
] as const

const AppStateContext = createContext<AppStateValue | null>(null)

function loadState(): PersistedAppState {
  try {
    const rawState = window.localStorage.getItem(STORAGE_KEY)
    if (!rawState) {
      return {
        version: 1,
        completedTaskIds: [],
        messages: [...INITIAL_MESSAGES],
      }
    }

    const parsedState = JSON.parse(rawState) as Partial<PersistedAppState>
    if (parsedState.version !== 1) {
      throw new Error('不支持的本地数据版本')
    }

    return {
      version: 1,
      completedTaskIds: Array.isArray(parsedState.completedTaskIds)
        ? parsedState.completedTaskIds.filter(
            (item): item is string => typeof item === 'string',
          )
        : [],
      messages: Array.isArray(parsedState.messages)
        ? parsedState.messages.filter(isChatMessage)
        : [...INITIAL_MESSAGES],
    }
  } catch {
    return {
      version: 1,
      completedTaskIds: [],
      messages: [...INITIAL_MESSAGES],
    }
  }
}

function isChatMessage(value: unknown): value is ChatMessage {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const candidate = value as Partial<ChatMessage>
  return (
    typeof candidate.id === 'string' &&
    (candidate.role === 'student' || candidate.role === 'tutor') &&
    typeof candidate.content === 'string' &&
    typeof candidate.createdAt === 'string'
  )
}

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PersistedAppState>(loadState)

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [state])

  const completedTaskIds = useMemo(
    () => new Set(state.completedTaskIds),
    [state.completedTaskIds],
  )

  const value = useMemo<AppStateValue>(
    () => ({
      completedTaskIds,
      messages: state.messages,
      toggleTask(taskId) {
        setState((currentState) => {
          const nextIds = new Set(currentState.completedTaskIds)
          if (nextIds.has(taskId)) {
            nextIds.delete(taskId)
          } else {
            nextIds.add(taskId)
          }

          return {
            ...currentState,
            completedTaskIds: [...nextIds],
          }
        })
      },
      addStudentMessage(content) {
        const trimmedContent = content.trim()
        if (!trimmedContent) {
          return
        }

        const timestamp = new Date().toISOString()
        const studentMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'student',
          content: trimmedContent,
          createdAt: timestamp,
        }
        const tutorMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'tutor',
          content:
            '我先不直接给答案。请把你认为最关键的已知条件和第一步写出来，我会根据这一步继续提示。',
          createdAt: timestamp,
        }

        setState((currentState) => ({
          ...currentState,
          messages: [...currentState.messages, studentMessage, tutorMessage],
        }))
      },
      resetLocalState() {
        setState({
          version: 1,
          completedTaskIds: [],
          messages: [...INITIAL_MESSAGES],
        })
      },
    }),
    [completedTaskIds, state.messages],
  )

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  )
}

export function useAppState() {
  const context = useContext(AppStateContext)
  if (!context) {
    throw new Error('useAppState 必须在 AppStateProvider 内使用')
  }
  return context
}
